import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Button,
  Tooltip,
  Typography,
  Radio,
  DatePicker,
} from 'antd';

import { CloseOutlined } from '@ant-design/icons';
import { sortBy } from 'lodash';

import {
  Form,
  WrapperSelect,
  ThumbnailItem,
  Fieldset,
  FullWidthSpace,
} from 'common/components';
import BrickCodeInput from './BrickCodeInput';
import ProductEditBrandModal from 'pages/product-full-view/components/product-edit-brand/ProductEditBrandModal';
import { ChargeActiveProduct } from './make-product-active/product-form/ChargeActiveProduct';
import { ResetFormStatus } from './make-product-active/product-form/ResetFormStatus';

import * as companySelectors from 'pages/company-profile/controllers/selectors';
import userSelectors from '@redux/user/selectors';

import { useValidateStatusField } from 'hooks/useValidateStatusField';

import { useGetMemberInfo } from './hooks';
import { useGetEnumsByEnumName } from 'hooks';
import { PACKAGE_LEVELS, STATUS_PRODUCT, DATE_FORMAT } from 'static/Constants';
import { useCheckIsProductOverlay } from 'hooks/product';

import { checkDeployEnv } from 'utils/checkDeployEnv';

import { useIntl } from 'react-intl';
import messages from 'i18n/messages/product';

import { useCheckPermissionEditBrand } from 'hooks/useCheckPermissionEditBrand';

import { OverlayInputWrapper } from '../overlay';

import { get, isEqual } from 'lodash';

import { useGetMemberHeaderFromMemberId } from 'common/queries/member-module';

import { ERROR_FIELD_NAME_COLLECTION } from '../preview-requirements/constants';

import {
  disableStatusSelection,
  enableMakeActiveProduct,
} from 'utils/ixone-capture';

import './CreateProductForm.less';

const { Option } = Select;
const { Text } = Typography;

const CreateProductForm = (props) => {
  const intl = useIntl();

  const {
    form,
    productData,
    formMode,
    targetMarketList,
    productEnums,
    currentHierarchy,
    brick,
    updateBrickCodeData,
    handleResetBrandId,
    setBrandId,
    editBrandVisible,
    setEditBrandVisible,
    brandThumb,
    setBrandThumb,
    errorInfo,
    memberIdBrand,
    originProductFull,
    productFull,
  } = props;

  const isProductOverlay = useCheckIsProductOverlay({
    productFull: formMode === 'edit' ? productFull : null,
  });

  const userMemberInfo =
    useSelector(companySelectors.selectMemberProfile()) ?? {};

  const userInfo = useSelector(userSelectors.makeSelectUserInfo());

  const { memberInfo: companyInfo } = useGetMemberInfo(memberIdBrand);
  const { data: memberHeaderOfProductOwner } = useGetMemberHeaderFromMemberId({
    memberId: productData?.ownerId,
    enabled: Boolean(productData && productData?.ownerId),
  });

  const isEdit = formMode === 'edit';

  const isActiveStatus = productData?.status?.toLowerCase() === 'active';

  /* match conditions for making active product and status is not active  */
  const isProductionEnv = checkDeployEnv('production');
  const isDisplayActiveProductBtn =
    !isProductionEnv && //* hide on production
    enableMakeActiveProduct(productData) &&
    !isActiveStatus &&
    isEdit;

  const { data: memberHeaderOfProductCreator } = useGetMemberHeaderFromMemberId(
    {
      memberId: memberIdBrand,
      enabled: !!memberIdBrand,
    }
  );

  const hasPermissionEditBrand = useCheckPermissionEditBrand(
    memberIdBrand,
    userInfo
  );

  const { statusValidateProps } = useValidateStatusField({
    form,
    entityType: 'Product',
    entityId: productData?.productId,
    placement: 'rightBottom',
  });

  const firearmCategoriesEnum = useGetEnumsByEnumName('FirearmCategoriesEnum');
  const firearmSubCategoriesEnum = useGetEnumsByEnumName(
    'FirearmSubCategoriesEnum'
  );

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const initFieldsError = () => {
    if (!errorInfo) return;

    form.setFields(errorInfo);
  };

  const sortTargetMarket = () => {
    if (!targetMarketList) return [];
    const USMarket = targetMarketList.find((marketItem, index) => {
      if (marketItem.Numeric !== 840) return false;

      return true;
    });

    const noneUSMarketList = targetMarketList.filter((marketItem) => {
      return marketItem.Numeric !== 840;
    });

    const sortedNoneUSMarketList = noneUSMarketList.sort((a, b) => {
      return a.Country - b.Country;
    });

    return [USMarket, ...sortedNoneUSMarketList];
  };

  const productBasicUOM = productEnums?.filter(
    (type) => type.enumName === 'BasicUomEnum'
  );

  const packageLevelWarning = currentHierarchy?.productHierarchyId
    ? {
        help: intl.formatMessage(messages.packageLevelTooltip),
        validateStatus: 'warning',
        hasFeedback: true,
      }
    : {};

  const enumProperties =
    productBasicUOM?.[0]?.enumProperties?.filter((type) =>
      ['Mass', 'Count', 'Volume', 'Dimensions', 'Area'].includes(type.enumGroup)
    ) || [];

  const enumMassProperties =
    productBasicUOM?.[0]?.enumProperties?.filter(
      (type) => type.enumGroup === 'Mass'
    ) || [];

  const enumDimensionsProperties =
    productBasicUOM?.[0]?.enumProperties?.filter(
      (type) => type.enumGroup === 'Dimensions'
    ) || [];

  const selectionFilter = useCallback(
    (input, option) =>
      option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0,
    []
  );

  const isPrivateVisibilityProduct = () => {
    if (formMode === 'create') {
      if (companyInfo || memberIdBrand) {
        return (
          memberHeaderOfProductCreator?.products?.toLowerCase() === 'private'
        );
      }
    }

    if (formMode === 'edit') {
      return memberHeaderOfProductOwner?.products?.toLowerCase() === 'private';
    }

    //* default return;
    return userMemberInfo?.products?.toLowerCase() === 'private';
  };

  const sortedTargetMarket = sortTargetMarket();

  //* add product
  const showRetailerProductCreation = (() => {
    if (isEdit) return false;

    /**
     ** check permission - create product for current user
     */
    if (!memberIdBrand && userInfo?.member?.memberType) {
      if (userInfo?.member?.memberType?.toLowerCase() === 'retailer')
        return true;
    }

    /**
     ** check permission - create product for member maintenance view
     */
    if (companyInfo || memberIdBrand) {
      if (companyInfo?.memberType?.toLowerCase() === 'retailer') return true;
      else return false;
    }

    return false;
  })();

  //* permssion edit private label
  const showEditPrivateLabel = (() => {
    /**
     ** check permission - create product for current user
     */
    if (!isEdit) {
      if (!memberIdBrand && userInfo?.member?.memberType) {
        if (userInfo?.member?.memberType?.toLowerCase() === 'retailer')
          return true;
      }
    }

    /**
     ** check permission - create product for member in maintain member view
     */
    if (!isEdit) {
      if (companyInfo || memberIdBrand) {
        if (companyInfo?.memberType?.toLowerCase() === 'retailer') return true;
      }
    }
    /**
     ** check permssion - edit product
     */
    if (isEdit) {
      if (productData?.ownerRole?.toLowerCase() === 'retailer') return true;
    }

    return false;
  })();

  useEffect(() => {
    initFieldsError();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        packageLevel: PACKAGE_LEVELS[0],
        isPrivateLabel: true,
      });
    }
  }, [isEdit]);

  const brandError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.BRAND)
  );

  const upcGtinError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.AT_LEAST_GTIN_UPC)
  );

  const brandErrorMessage = brandError ? brandError.errors[0] : '';
  const upcGtinErrorMessage = upcGtinError ? upcGtinError.errors[0] : '';
  const getOverlayProps = (path) => {
    const originValue = get(originProductFull, path);

    return {
      checkShowOverlayFn: ({ value }) => {
        return (
          !isEqual(value ?? null, originValue ?? null) &&
          isProductOverlay &&
          !!originProductFull
        );
      },
      originValue: originValue || 'N/A',
    };
  };

  const getBrickCodeOverlayProps = () => {
    const brickCode =
      brick?.brickCode?.toString() !== originProductFull?.brickCode?.toString();

    const originValue = originProductFull?.brickCode || 'N/A';
    return {
      showOverlay: isProductOverlay ? brickCode : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const renderProductFieldset = () => {
    return (
      // <Fieldset title='Product information' >
      <Row gutter={16} id='product-field'>
        <Col span={8}>
          <Form.Item
            label='Product Name'
            name='productName'
            rules={[
              {
                required: true,
                message: intl.formatMessage(messages.productNameRequired),
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('productName')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item label='Product Family' name='productFamily'>
            <OverlayInputWrapper {...getOverlayProps('productFamily')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Description' name='description'>
            <OverlayInputWrapper {...getOverlayProps('description')}>
              <Input.TextArea rows={7} />
            </OverlayInputWrapper>
          </Form.Item>

          <Row>
            <Col span={14} />
            <Col span={8}>
              <Form.Item name='brandId'>
                <Form.Item>
                  <div className='add-product__thumb-wrapper'>
                    <div>
                      <CloseOutlined
                        className='add-product__thumb-wrapper-close-btn'
                        onClick={handleResetBrandId}
                      />
                    </div>
                    <ThumbnailItem
                      hideLabel={true}
                      imageThumbnail={brandThumb}
                      hideCheckbox
                      style={{ marginLeft: 0 }}
                    />
                  </div>
                  <Tooltip title='Choose an Brand ID'>
                    <Button onClick={() => setEditBrandVisible(true)}>
                      Link to Brand
                    </Button>
                  </Tooltip>
                </Form.Item>
              </Form.Item>
              {brandError ? (
                <Text type='danger'>{brandErrorMessage}</Text>
              ) : null}
              {editBrandVisible && (
                <ProductEditBrandModal
                  visible={editBrandVisible}
                  setVisibleEditBrand={setEditBrandVisible}
                  productDetail={productData}
                  isAddProduct={true}
                  isEdit={isEdit}
                  setBrandId={setBrandId}
                  setBrandThumb={setBrandThumb}
                  memberIdFromBrand={memberIdBrand}
                />
              )}
            </Col>
          </Row>
        </Col>
        <Col span={8}>
       
          <Form.Item name='activeUpc' label='Active UPC'>
            <OverlayInputWrapper {...getOverlayProps('activeUpc')}>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='UPC' name='upc'>
            <OverlayInputWrapper {...getOverlayProps('upc')}>
              <Input
                min='0'
                onKeyPress={handleNumberInput}
                maxLength={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Universal Product ID' name='universalProductId'>
            <OverlayInputWrapper {...getOverlayProps('universalProductId')}>
              <Input
                min='0'
                onKeyPress={handleNumberInput}
                maxLength={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='SKU' name='sku'>
            <OverlayInputWrapper {...getOverlayProps('sku')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Model' name='model'>
            <OverlayInputWrapper {...getOverlayProps('model')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Material' name='material'>
            <OverlayInputWrapper {...getOverlayProps('material')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item label='Width' name='width'>
            <OverlayInputWrapper {...getOverlayProps('width')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item label='Height' name='height'>
            <OverlayInputWrapper {...getOverlayProps('height')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Weight' name='weight'>
            <OverlayInputWrapper {...getOverlayProps('weight')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Stock Unit of Measure' name='stockUOM'>
            <OverlayInputWrapper {...getOverlayProps('stockUOM')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='visibility' label='Visibility'>
            <OverlayInputWrapper {...getOverlayProps('visibility')}>
              {isPrivateVisibilityProduct() ? (
                <WrapperSelect
                  disabled={productData?.gdsnShield}
                  getPopupContainer={() =>
                    document.getElementById('product-field')
                  }
                >
                  <Option value='Only If Shared'>Only If Shared</Option>
                  <Option value='Private'>Private</Option>
                  {/* <Option value='None'>None</Option> */}
                </WrapperSelect>
              ) : (
                <WrapperSelect
                  disabled={productData?.gdsnShield}
                  getPopupContainer={() =>
                    document.getElementById('product-field')
                  }
                >
                  <Option value='Only If Shared'>Only If Shared</Option>
                  <Option value='Public'>Public</Option>
                  <Option value='Private'>Private</Option>
                  {/* <Option value='None'>None</Option> */}
                </WrapperSelect>
              )}
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            name='status'
            label='Status'
            //  {...statusValidateProps}
          >
            <OverlayInputWrapper {...getOverlayProps('status')}>
              <WrapperSelect
                getPopupContainer={() =>
                  document.getElementById('product-field')
                }
                disabled={productData?.status?.toLowerCase() === 'exception'}
                // suffixIcon={
                //   isEdit && !isProductionEnv ? (
                //     <ResetFormStatus form={form} productData={productData} />
                //   ) : null
                // }
                // allowClear={isEdit && isProductionEnv ? false : true}
              >
                {[
                  sortBy([...STATUS_PRODUCT], ['displayName']).map(
                    (status, index) => {
                      return (
                        <Option
                          value={status?.value}
                          key={index}
                          // disabled={
                          //   !isProductionEnv &&
                          //   disableStatusSelection({
                          //     valueOption: status.value,
                          //     ...productData,
                          //   })
                          // }
                        >
                          {status?.displayName}
                        </Option>
                      );
                    }
                  ),
                ]}
              </WrapperSelect>
            </OverlayInputWrapper>
          </Form.Item>
          {/* <Form.Item label='Release Date' name='releaseDate'>
            <OverlayInputWrapper {...getOverlayProps('releaseDate')}>
              <DatePicker
                format={DATE_FORMAT.SYSTEM_FORMAT}
                style={{ width: '100%' }}
              />
            </OverlayInputWrapper>
          </Form.Item> */}
          <Form.Item label='Manufacturer' name='manufacturer'>
            <OverlayInputWrapper {...getOverlayProps('manufacturer')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Vendor Part #' name='vendorPart'>
            <OverlayInputWrapper {...getOverlayProps('vendorPart')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            label='Brands Reporting to Master Account'
            name='brandsReportingToMasterAccount'
          >
            <OverlayInputWrapper
              {...getOverlayProps('brandsReportingToMasterAccount')}
            >
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            label='Additional Attributes For End Consumer'
            name='additionalAttributesForEndConsumer'
          >
            <OverlayInputWrapper
              {...getOverlayProps('additionalAttributesForEndConsumer')}
            >
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='brick' label='Brick Code'>
            <OverlayInputWrapper
              style={{ paddingRight: '10px' }}
              {...getBrickCodeOverlayProps()}
            >
              <BrickCodeInput
                updateBrickCodeData={updateBrickCodeData}
                initialValue={brick}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Brick Code Name'>
            <OverlayInputWrapper {...getOverlayProps('brickCodeName')}>
              <Input value={brick?.brickCodeName} disabled />
            </OverlayInputWrapper>
          </Form.Item>
          {/* <Form.Item name='category' label='Category'>
            <OverlayInputWrapper {...getOverlayProps('category')}>
              <WrapperSelect
                getPopupContainer={() =>
                  document.getElementById('product-field')
                }
              >
                {[
                  sortBy(firearmCategoriesEnum, ['enumDisplayName']).map(
                    (status, index) => {
                      return (
                        <Option value={status?.enumDisplayName} key={index}>
                          {status?.enumDisplayName}
                        </Option>
                      );
                    }
                  ),
                ]}
              </WrapperSelect>
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='subcategory' label='Sub Category'>
            <OverlayInputWrapper {...getOverlayProps('subcategory')}>
              <WrapperSelect
                getPopupContainer={() =>
                  document.getElementById('product-field')
                }
              >
                {[
                  sortBy(firearmSubCategoriesEnum, ['enumDisplayName']).map(
                    (status, index) => {
                      return (
                        <Option value={status?.enumDisplayName} key={index}>
                          {status?.enumDisplayName}
                        </Option>
                      );
                    }
                  ),
                ]}
              </WrapperSelect>
            </OverlayInputWrapper>
          </Form.Item> */}
             <Form.Item name='apolloCustom' label='Apollo Custom'>
            <OverlayInputWrapper {...getOverlayProps('apolloCustom')}>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='taloExclusive' label='Talo Exclusive'>
            <OverlayInputWrapper {...getOverlayProps('taloExclusive')}>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </OverlayInputWrapper>
          </Form.Item>
        </Col>
      </Row>
      // </Fieldset>
    );
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      name='Add-Product'
      preserve={false}
      className='add-product__modal'
    >
      <FullWidthSpace size={16}>
        <Row gutter={16}>
          <Col span={24}>{renderProductFieldset()}</Col>
        </Row>
      </FullWidthSpace>
    </Form>
  );
};

const styles = {
  numericInput: {
    width: '100%',
  },
};

const MAX_NUMBER_INPUT_UPC = 9999999999999999999;

const formItemLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 8,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 15,
    },
  },
};

export default CreateProductForm;
